// File: frontend/src/pages/dashboards/AdminDashboard.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSchool } from '../../context/SchoolContext';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/axios';

const AdminDashboard = () => {
  const { user } = useAuth();
  const { school } = useSchool();
  const navigate = useNavigate();

  const [attendanceStats, setAttendanceStats] = useState(null);
  const [feeStats, setFeeStats] = useState(null);
  const [activeStaffCount, setActiveStaffCount] = useState(0);

  // Executive AI Agent State
  const [aiSummary, setAiSummary] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const fetchAiSummary = async () => {
    try {
      setAiLoading(true);
      setAiSummary('');
      const res = await api.post('/api/v1/agent/run', {
        prompt: "Give me a complete operational summary for today including attendance, financial receipts, and top fee defaulters."
      });
      if (res.data?.success && res.data?.summary) {
        setAiSummary(res.data.summary);
      } else {
        setAiSummary(res.data?.message || 'Failed to generate operational summary.');
      }
    } catch (err) {
      console.error('Error fetching AI agent summary:', err);
      setAiSummary(`⚠️ Unable to generate AI summary: ${err.response?.data?.message || err.message || 'Server error'}`);
    } finally {
      setAiLoading(false);
    }
  };

  // Helper to format currency dynamically
  const formatPKR = (amount) => {
    const val = Number(amount) || 0;
    if (val >= 10000000) {
      return `PKR ${(val / 10000000).toFixed(2)} Cr`;
    }
    if (val >= 100000) {
      return `PKR ${(val / 100000).toFixed(2)} Lakh`;
    }
    return `PKR ${val.toLocaleString('en-PK')}`;
  };

  // Fetch real statistics on load
  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const [attRes, feeRes, staffRes] = await Promise.all([
          api.get('/api/v1/attendance/summary').catch(() => ({ data: null })),
          api.get('/api/v1/fees/dashboard').catch(() => ({ data: null })),
          api.get('/api/v1/staff', { params: { status: 'active', limit: 1 } }).catch(() => ({ data: null }))
        ]);

        if (attRes.data?.success) setAttendanceStats(attRes.data.data);
        if (feeRes.data?.success) setFeeStats(feeRes.data.data);
        if (staffRes.data?.success) {
          setActiveStaffCount(staffRes.data.pagination?.total || (staffRes.data.data || []).length);
        }
      } catch (error) {
        console.error('Error loading dashboard stats:', error);
      }
    };

    fetchDashboardStats();
  }, []);

  const totalStudents = attendanceStats?.total_students || 0;
  const totalStaff = activeStaffCount || 0;

  const isSuperAdmin = user?.role === 'super_admin';
  const isAiInsightsUnlocked = isSuperAdmin || (school?.feature_ai_insights !== 0 && school?.feature_ai_agent !== 0);
  const isAiTestGenUnlocked = isSuperAdmin || school?.feature_ai_test_gen !== 0;

  return (
    <DashboardLayout>
      {/* 1. Header Banner & Quick Actions */}
      <div
        className="flex flex-col md:flex-row md:items-center justify-between gap-4 rounded-3xl p-6 text-white shadow-xl transition-all"
        style={{
          background: `linear-gradient(135deg, ${school?.primary_color || 'var(--primary-color)'} 0%, ${school?.secondary_color || 'var(--secondary-color)'} 100%)`
        }}
      >
        <div>
          <div className="flex items-center space-x-2 mb-1">
            <span className="px-2.5 py-0.5 bg-white/20 text-white text-[10px] font-bold rounded-full border border-white/30 uppercase tracking-widest">
              Executive Portal
            </span>
            <span className="text-white/80 text-xs">• {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            Welcome back, {user?.name || 'Administrator'} 👋
          </h1>
          <p className="text-white/90 text-xs sm:text-sm mt-1">
            Here is what's happening at <strong className="text-white font-bold">{school?.name || 'MarkazSchool Pro'}</strong> today.
          </p>
        </div>

        {/* Quick Action Floating Buttons */}
        <div className="flex items-center space-x-2 shrink-0">
          {isAiTestGenUnlocked ? (
            <button
              onClick={() => navigate('/ai-tests')}
              className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-extrabold rounded-xl shadow-lg transition flex items-center space-x-1.5"
            >
              <span>✨</span>
              <span>AI Test Generator</span>
            </button>
          ) : (
            <a
              href="https://wa.me/923171818108?text=Hello%20Waleed%20Technologies,%20I%20want%20to%20upgrade%20my%20school%20plan%20tier%20to%20unlock%20AI%20Test%20Generator%20Pro%20feature"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2.5 bg-amber-500/30 text-amber-200 hover:bg-amber-500/40 text-xs font-bold rounded-xl border border-amber-500/40 flex items-center space-x-1.5 transition"
              title="Locked Pro Feature - Click to Upgrade via WhatsApp"
            >
              <span>💬</span>
              <span>AI Test Gen (Locked Pro)</span>
            </a>
          )}
          <button
            onClick={() => navigate('/students/new')}
            style={{ backgroundColor: school?.button_color || 'var(--button-color)' }}
            className="px-4 py-2.5 text-white text-xs font-bold rounded-xl shadow-md transition hover:brightness-110"
          >
            + Add Student
          </button>
          <button
            onClick={() => navigate('/fees')}
            className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-xl border border-white/10 transition"
          >
            Fees
          </button>
        </div>
      </div>

      {/* Executive AI Operational Insights Card (Feature Gated) */}
      {isAiInsightsUnlocked ? (
        <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white rounded-3xl p-6 shadow-xl border border-indigo-500/20 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
            <div className="flex items-center space-x-3">
              <span className="w-10 h-10 rounded-2xl bg-indigo-500/20 text-indigo-300 flex items-center justify-center text-xl font-bold border border-indigo-500/30">
                ✨
              </span>
              <div>
                <h2 className="font-extrabold text-base tracking-wide flex items-center gap-2">
                  Executive AI Operational Insights
                  <span className="text-[10px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded-full uppercase font-bold">
                    Groq Llama-3.3 Live
                  </span>
                </h2>
                <p className="text-xs text-slate-400">Automated daily attendance, fee revenue & defaulter analysis</p>
              </div>
            </div>

            <button
              onClick={fetchAiSummary}
              disabled={aiLoading}
              style={{ backgroundColor: school?.button_color || 'var(--button-color)' }}
              className="px-4 py-2.5 text-white font-extrabold text-xs rounded-xl shadow-lg transition hover:brightness-110 flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              {aiLoading ? (
                <>
                  <svg className="w-4 h-4 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Analyzing Database...</span>
                </>
              ) : (
                <>
                  <span>⚡</span>
                  <span>{aiSummary ? 'Re-Analyze Today\'s Data' : 'Generate Daily Insight'}</span>
                </>
              )}
            </button>
          </div>

          {aiSummary ? (
            <div className="bg-slate-950/60 rounded-2xl p-5 border border-white/10 text-xs sm:text-sm leading-relaxed text-slate-200 whitespace-pre-wrap">
              {aiSummary}
            </div>
          ) : (
            <div className="text-xs text-slate-400 italic py-2 flex items-center space-x-2">
              <span>💡 Click <strong>"Generate Daily Insight"</strong> to let AI analyze today's MySQL database records (Attendance, Fee Receipts, Overdue Defaulters).</span>
            </div>
          )}
        </div>
      ) : (
        /* Sleek PRO AI Lock Banner for Basic Plan Client Schools */
        <div className="bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 text-white rounded-3xl p-6 shadow-xl border border-purple-500/30 space-y-3 relative overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <span className="w-10 h-10 rounded-2xl bg-amber-500/20 text-amber-400 flex items-center justify-center text-xl font-bold border border-amber-500/30">
                🔒
              </span>
              <div>
                <h2 className="font-extrabold text-base tracking-wide flex items-center gap-2">
                  Executive AI Operational Insights
                  <span className="text-[10px] bg-amber-500/20 text-amber-300 border border-amber-500/30 px-2 py-0.5 rounded-full uppercase font-bold">
                    PRO AI MODULE
                  </span>
                </h2>
                <p className="text-xs text-slate-400">Automated daily attendance, fee revenue & defaulter analysis powered by Groq Llama-3.3</p>
              </div>
            </div>
            <a
              href="https://wa.me/923171818108?text=Hello%20Waleed%20Technologies,%20I%20want%20to%20upgrade%20my%20school%20plan%20tier%20to%20unlock%20Executive%20AI%20Operational%20Insights%20module"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs rounded-xl shadow-lg transition flex items-center justify-center space-x-2 shrink-0"
            >
              <span>💬</span>
              <span>Upgrade via WhatsApp (+92 317 1818108)</span>
            </a>
          </div>
          <div className="p-4 bg-slate-950/60 rounded-2xl border border-white/5 text-xs text-slate-400 flex items-center justify-between">
            <span>💡 Contact software vendor (Waleed Technologies) to activate the Executive AI module for your school portal.</span>
            <span className="font-mono text-[10px] text-amber-400 font-bold uppercase">Enterprise Feature</span>
          </div>
        </div>
      )}

      {/* 2. Top 4 High-Impact KPI Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Card 1: Total Students */}
        <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-purple-subtle hover:border-purple-200 transition">
          <div className="flex items-center justify-between">
            <span className="w-11 h-11 rounded-2xl bg-purple-100 text-purple-700 flex items-center justify-center text-xl font-bold">
              🎓
            </span>
            <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
              Active
            </span>
          </div>
          <div className="mt-4">
            <h3 className="text-2xl font-extrabold text-slate-800">{totalStudents}</h3>
            <p className="text-xs font-semibold text-slate-500">Total Enrolled Students</p>
          </div>
        </div>

        {/* Card 2: Attendance Rate */}
        <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-purple-subtle hover:border-purple-200 transition">
          <div className="flex items-center justify-between">
            <span className="w-11 h-11 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center text-xl font-bold">
              📊
            </span>
            <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
              Optimal
            </span>
          </div>
          <div className="mt-4">
            <h3 className="text-2xl font-extrabold text-slate-800">
              {attendanceStats?.present_percentage !== undefined ? `${attendanceStats.present_percentage}%` : '100%'}
            </h3>
            <p className="text-xs font-semibold text-slate-500">Today's Student Attendance</p>
          </div>
        </div>

        {/* Card 3: Fee Collections */}
        <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-purple-subtle hover:border-purple-200 transition">
          <div className="flex items-center justify-between">
            <span className="w-11 h-11 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center text-xl font-bold">
              💳
            </span>
            <span className="text-[11px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
              {feeStats?.paid_percentage ? `${feeStats.paid_percentage}% Paid` : '0% Paid'}
            </span>
          </div>
          <div className="mt-4">
            <h3 className="text-2xl font-extrabold text-slate-800">
              {formatPKR(feeStats?.collected_this_month || feeStats?.total_ledger_collected || 0)}
            </h3>
            <p className="text-xs font-semibold text-slate-500">Collected Fees (This Month)</p>
          </div>
        </div>

        {/* Card 4: Active Teachers & Staff */}
        <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-purple-subtle hover:border-purple-200 transition">
          <div className="flex items-center justify-between">
            <span className="w-11 h-11 rounded-2xl bg-indigo-100 text-indigo-700 flex items-center justify-center text-xl font-bold">
              👨‍🏫
            </span>
            <span className="text-[11px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
              Active
            </span>
          </div>
          <div className="mt-4">
            <h3 className="text-2xl font-extrabold text-slate-800">{totalStaff}</h3>
            <p className="text-xs font-semibold text-slate-500">Teachers & Faculty Staff</p>
          </div>
        </div>
      </div>

      {/* 3. Main Split View: Fee Analytics Graph (Left 70%) & Action Center (Right 30%) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Left Column (Main Fee Trends Graph) */}
        <div className="lg:col-span-2 bg-white rounded-3xl p-6 border border-slate-100 shadow-purple-subtle space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-slate-900">Fee Collection Analytics</h3>
              <p className="text-xs text-slate-400">Quarterly fee collection target vs actual collected</p>
            </div>
            <select className="bg-purple-50 border border-purple-100 text-purple-900 text-xs font-bold rounded-xl px-3 py-1.5 focus:outline-none">
              <option>Last 6 Quarters</option>
              <option>This Academic Year</option>
            </select>
          </div>

          {/* Bar Chart Visual */}
          <div className="h-56 flex items-end justify-between gap-3 pt-6 pb-2 border-b border-slate-100">
            {[
              { label: 'Q1', target: '52L', collected: '43L' },
              { label: 'Q2', target: '55L', collected: '50L' },
              { label: 'Q3', target: '52L', collected: '48L' },
              { label: 'Q4', target: '60L', collected: '50L' },
              { label: 'Q5', target: '58L', collected: '47L' },
              { label: 'Q6', target: '62L', collected: '54L' }
            ].map((q, idx) => (
              <div key={idx} className="flex-1 flex flex-col items-center gap-2 group relative">
                <div className="w-full max-w-[36px] bg-purple-100 rounded-t-xl h-44 relative flex items-end">
                  <div
                    className="w-full bg-purple-600 rounded-t-xl group-hover:bg-purple-500 transition-all duration-300"
                    style={{ height: `${(parseInt(q.collected) / 62) * 100}%` }}
                  ></div>
                </div>
                <span className="text-xs font-bold text-slate-600">{q.label}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
            <span>Total Ledger Collected: <strong className="text-purple-700 font-extrabold">{formatPKR(feeStats?.total_ledger_collected || 0)}</strong></span>
            <button
              onClick={() => navigate('/fees')}
              className="text-purple-600 font-bold hover:text-purple-800 flex items-center space-x-1"
            >
              <span>View Full Ledger</span>
              <span>→</span>
            </button>
          </div>
        </div>

        {/* Right Column (Executive Action Needed Panel) */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-purple-subtle space-y-5">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="text-base font-bold text-slate-900">Requires Action</h3>
            <span className="text-[10px] bg-amber-100 text-amber-800 font-bold px-2 py-0.5 rounded-full">
              {(feeStats?.total_defaulters || 0) > 0 ? 'Action Needed' : 'All Clear'}
            </span>
          </div>

          <div className="space-y-3 text-xs">
            {/* Urgent Item 1 */}
            <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-xl bg-amber-500 text-white font-bold flex items-center justify-center text-xs">
                  ⚠️
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">
                    Fee Defaulters ({feeStats?.total_defaulters !== undefined ? feeStats.total_defaulters : 0})
                  </h4>
                  <p className="text-[11px] text-slate-500">Overdue payments pending</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/fees')}
                className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-900 font-bold text-xs rounded-xl transition"
              >
                Review
              </button>
            </div>

            {/* Urgent Item 2 */}
            <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-xl bg-purple-600 text-white font-bold flex items-center justify-center text-xs">
                  📝
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">Leave Requests (2)</h4>
                  <p className="text-[11px] text-slate-500">Physics & Math faculty</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/staff')}
                className="px-2.5 py-1 bg-purple-100 hover:bg-purple-200 text-purple-900 font-bold rounded-lg transition"
              >
                Approve
              </button>
            </div>
          </div>

          {/* Quick Module Shortcuts */}
          <div className="pt-3 border-t border-slate-100 space-y-2">
            <p className="text-[10px] font-bold text-purple-600 uppercase tracking-wider">Frequent Tools</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => navigate('/timetable')}
                className="p-2.5 bg-purple-50/70 hover:bg-purple-100 text-purple-900 text-xs font-semibold rounded-xl text-left border border-purple-100 transition flex items-center space-x-2"
              >
                <span>🗓️</span>
                <span>Timetable</span>
              </button>
              <button
                onClick={() => navigate('/idcards')}
                className="p-2.5 bg-purple-50/70 hover:bg-purple-100 text-purple-900 text-xs font-semibold rounded-xl text-left border border-purple-100 transition flex items-center space-x-2"
              >
                <span>📇</span>
                <span>ID Cards</span>
              </button>
            </div>
          </div>
        </div>

      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;

